import { nextTick, reactive, type UnwrapNestedRefs } from 'vue';
import type { VideoJsPlayer } from 'video.js';
import { getMovieFileUrlObj, getScreenshotBlobOfVid } from 'src/lib/moviePlayerHelper';
import type { CameraPosition } from 'src/models/movie';
import type {
  BothLeftRight,
  LeftRight,
  MoviePlayerCompareModeState,
} from 'src/models/moviePlayer';

interface Deps {
  getVjsForLr: (lr: LeftRight) => VideoJsPlayer | undefined;
  getUIPlayState: (lr: LeftRight) => 'playing' | 'paused';
  pauseUI: (lr_: BothLeftRight) => void;
  playUI: (lr_: BothLeftRight) => void;
}

interface MoviePlayerCompareModeCameraContext {
  enterFullScreen: (lr: LeftRight) => Promise<void>;
  changeCameraPosition: (camPos: CameraPosition) => Promise<void>;
  getScreenshotBlob: (lr: LeftRight, vid: string) => Promise<Blob | string | null | undefined>;
}

// useMoviePlayerCompareModePlayback.tsから分割した
// カメラ位置切替・全画面表示・スクリーンショット関連の関数。
// vjsインスタンス自体はplaybackが保持し、
// 本ファイルはdeps.getVjsForLr経由でのみアクセスする。
export function useMoviePlayerCompareModeCamera(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerCompareModeCameraContext> {
  const enterFullScreen = async(lr: LeftRight) => {
    const vjs = deps.getVjsForLr(lr);
    if (vjs) {
      vjs.requestFullscreen();
    }
  };
  const changeCameraPosition = async(camPos: CameraPosition) => {
    if (state.ui.selectedCameraPosition === camPos) return;

    state.ui.selectedCameraPosition = camPos;
    const playStateLeft = deps.getUIPlayState('left');
    const playStateRight = deps.getUIPlayState('right');
    deps.pauseUI('left');
    deps.pauseUI('right');
    state.left.isMovieReady = false;
    state.right.isMovieReady = false;
    const lrs: LeftRight[] = ['left', 'right'];
    const promises: Array<Promise<{lr: LeftRight; currentTime: number}>> = [];
    try {
      for (const lr of lrs) {
        const vjs = deps.getVjsForLr(lr);
        if (!vjs) continue;

        const currentTime = vjs.currentTime();
        const currentMovie = state[lr].movieList.current.currentMovie;
        if (!currentMovie) continue;

        const urlObj = await getMovieFileUrlObj(currentMovie.id);
        const url = urlObj[state.ui.selectedCameraPosition];
        if (!url) {
          state.ui.activeVjsLrMap[lr] = false;
          continue;
        }
        vjs.src(url);
        state.ui.activeVjsLrMap[lr] = true;
        promises.push(new Promise((resolve) => {
          vjs.one('loadeddata', () => { resolve({ lr, currentTime }); });
        }));
      }
      const objs = await Promise.all(promises);
      objs.forEach(({ lr, currentTime }) => {
        const vjs = deps.getVjsForLr(lr);
        if (vjs) vjs.currentTime(currentTime);
      });
    } catch (e) {
      console.error('動画ファイルURLの取得に失敗しました', e);
      return;
    }
    nextTick(() => {
      // 元々再生中だった場合は、再生を再開。
      // 動画の無いカメラ位置に切り替えた側は、再開すると
      // 白マスクの裏で前のカメラ位置の動画が再生され続けてしまうため対象外とする。
      if (playStateLeft === 'playing' && state.ui.activeVjsLrMap.left) deps.playUI('left');
      if (playStateRight === 'playing' && state.ui.activeVjsLrMap.right) deps.playUI('right');

      state.left.isMovieReady = true;
      state.right.isMovieReady = true;
    });
  };
  const getScreenshotBlob = async(lr: LeftRight, vid: string) => {
    const currentMovie = state[lr].movieList.current.currentMovie;
    const currentMovieCurrentDt = state[lr].geo.currentMovieCurrentDt;
    const vjs = deps.getVjsForLr(lr);
    if (!currentMovie || !currentMovieCurrentDt || !vjs) return;

    return getScreenshotBlobOfVid(
      currentMovie,
      currentMovieCurrentDt,
      vid,
      vjs);
  };

  return reactive({ enterFullScreen, changeCameraPosition, getScreenshotBlob });
}
